import * as THREE from 'three';

const LEG_BODY_PERCENT = 0.3;
const LEG_BODY_HOPPER_TO_GROUND = 0.5;

export function generateLipAndLid(bin, { lidLipMaterial, lidLipDoubleMaterial }) {
  const { dimensions } = bin;
  const { lipHeight } = dimensions;
  const { truncatedRadius } = dimensions.top;

  const lidAdditionalRadius = 0.025;
  const lidRadius = truncatedRadius + lidAdditionalRadius;
  const lidHeight = Math.min(0.1, lipHeight / 2);

  const group = new THREE.Group();
  group.rotateX(-Math.PI / 2);

  if (truncatedRadius === 0) {
    // Don't render a lip or lid when the radius of those items is 0
    return group;
  }

  const lip = new THREE.CylinderGeometry(truncatedRadius, truncatedRadius, lipHeight, 32, 1, true);
  const lipMesh = new THREE.Mesh(lip, lidLipMaterial);
  lipMesh.receiveShadow = true;
  lipMesh.castShadow = true;
  lipMesh.position.y = -lipHeight / 2;
  group.add(lipMesh);

  const lidGroup = new THREE.Group();
  group.add(lidGroup);

  const lidSides = new THREE.CylinderGeometry(lidRadius, lidRadius, lidHeight, 32, 1, true);
  const lidSidesMesh = new THREE.Mesh(lidSides, lidLipMaterial);
  lidSidesMesh.receiveShadow = true;
  lidSidesMesh.castShadow = true;
  lidSidesMesh.position.y = -lipHeight;
  lidGroup.add(lidSidesMesh);

  const lidTop = new THREE.CircleGeometry(lidRadius, 32);
  const lidTopMesh = new THREE.Mesh(lidTop, lidLipMaterial);
  lidTopMesh.receiveShadow = true;
  lidTopMesh.castShadow = true;
  lidTopMesh.position.y = -lipHeight - (lidHeight / 2);
  lidTopMesh.rotateX(Math.PI / 2);
  lidGroup.add(lidTopMesh);

  const lidDepth = new THREE.RingGeometry(truncatedRadius, lidRadius, 32, 2);
  const lidDepthMesh = new THREE.Mesh(lidDepth, lidLipDoubleMaterial);
  lidDepthMesh.receiveShadow = true;
  lidDepthMesh.castShadow = true;
  lidDepthMesh.position.y = -lipHeight + (lidHeight / 2);
  lidDepthMesh.rotateX(Math.PI / 2);
  lidGroup.add(lidDepthMesh);

  return group;
}

export function generateLegs(bin, legCount, legWidth, legDepth, { legMaterial }) {
  const legGroup = new THREE.Group();
  const { dimensions } = bin;
  const bodyRadius = dimensions.body.radius;
  const halfLegDepth = legDepth / 2;

  const legHeight = dimensions.hopper.height + (dimensions.body.height * LEG_BODY_PERCENT) + LEG_BODY_HOPPER_TO_GROUND;
  const legTopAtBotOfHooper = -dimensions.totalHeight - (legHeight / 2);
  const z = legTopAtBotOfHooper + dimensions.hopper.height + (dimensions.body.height * LEG_BODY_PERCENT);
  const bottomOfLegs = z - (legHeight / 2);

  for (let i = 0; i < legCount; i++) {
    const x = (bodyRadius + halfLegDepth) * Math.cos(i * 2 * Math.PI / legCount);
    const y = (bodyRadius + halfLegDepth) * Math.sin(i * 2 * Math.PI / legCount);

    const geometry = new THREE.BoxGeometry(legDepth, legWidth, legHeight);
    const leg = new THREE.Mesh(geometry, legMaterial);
    leg.position.set(x, y, z);
    leg.rotateZ(i * 2 * Math.PI / legCount);
    leg.receiveShadow = true;
    leg.castShadow = true;
    legGroup.add(leg);
  }

  return {
    legGroup,
    bottomOfLegs,
  };
}

export function generateRings(dimensions, cylinderSegments, { ringMaterial }) {
  const group = new THREE.Group();
  if (dimensions.ringCount <= 0) {
    return group;
  }

  const zPerRing = dimensions.body.height / dimensions.ringCount;
  const baseZ = -dimensions.totalHeight + dimensions.hopper.height;
  for (let i = 0; i < dimensions.ringCount + 1; i++) {
    const ringGeo = new THREE.TorusGeometry(dimensions.body.radius, 0.01, 32, cylinderSegments);
    const ringMesh = new THREE.Mesh(ringGeo, ringMaterial);
    ringMesh.position.z = baseZ + (i * zPerRing);
    group.add(ringMesh);
  }

  return group;
}

export function generateBinShell(dimensions, cylinderSegments, { binMaterial }) {
  const data = {
    top: { position: [], normal: [] },
    body: { position: [], normal: [] },
    bot: { position: [], normal: [] },
    type: 'mesh3d',
    opacity: 0.3,
    hoverinfo: 'skip',
    color: '#dedede',
  };

  for (let i = 0; i < cylinderSegments; i++) {
    let bodyRadius = dimensions.body.radius;
    let topRadius = dimensions.top.truncatedRadius;
    let hopperRadius = dimensions.hopper.truncatedRadius;

    let xBody = bodyRadius * Math.cos(i * 2 * Math.PI / cylinderSegments);
    let yBody = bodyRadius * Math.sin(i * 2 * Math.PI / cylinderSegments);
    let xBodyNext = bodyRadius * Math.cos((i + 1) * 2 * Math.PI / cylinderSegments);
    let yBodyNext = bodyRadius * Math.sin((i + 1) * 2 * Math.PI / cylinderSegments);

    let xTop = topRadius * Math.cos(i * 2 * Math.PI / cylinderSegments);
    let yTop = topRadius * Math.sin(i * 2 * Math.PI / cylinderSegments);
    let xTopNext = topRadius * Math.cos((i + 1) * 2 * Math.PI / cylinderSegments);
    let yTopNext = topRadius * Math.sin((i + 1) * 2 * Math.PI / cylinderSegments);

    let xHopper = hopperRadius * Math.cos(i * 2 * Math.PI / cylinderSegments);
    let yHopper = hopperRadius * Math.sin(i * 2 * Math.PI / cylinderSegments);
    let xHopperNext = hopperRadius * Math.cos((i + 1) * 2 * Math.PI / cylinderSegments);
    let yHopperNext = hopperRadius * Math.sin((i + 1) * 2 * Math.PI / cylinderSegments);

    // These values are all z values (so they are negative):
    const bottomOfTop = -dimensions.top.height /* m */;
    const bottomOfBody = bottomOfTop - (dimensions.body.height /* m*/);
    const bottomOfHopper = bottomOfBody - (dimensions.hopper.height);

    data.top.position.push(xTop, yTop, 0);
    data.top.position.push(xBody, yBody, bottomOfTop);
    data.top.position.push(xTopNext, yTopNext, 0);

    data.top.position.push(xBodyNext, yBodyNext, bottomOfTop);
    data.top.position.push(xTopNext, yTopNext, 0);
    data.top.position.push(xBody, yBody, bottomOfTop);

    data.body.position.push(xBody, yBody, bottomOfTop);
    data.body.position.push(xBody, yBody, bottomOfBody);
    data.body.position.push(xBodyNext, yBodyNext, bottomOfTop);

    data.body.position.push(xBodyNext, yBodyNext, bottomOfBody);
    data.body.position.push(xBodyNext, yBodyNext, bottomOfTop);
    data.body.position.push(xBody, yBody, bottomOfBody);

    data.bot.position.push(xBody, yBody, bottomOfBody);
    data.bot.position.push(xHopperNext, yHopperNext, bottomOfHopper);
    data.bot.position.push(xBodyNext, yBodyNext, bottomOfBody);

    data.bot.position.push(xHopperNext, yHopperNext, bottomOfHopper);
    data.bot.position.push(xBody, yBody, bottomOfBody);
    data.bot.position.push(xHopper, yHopper, bottomOfHopper);
  }

  const { top, body, bot } = data;
  const buffGeometry = new THREE.BufferGeometry();
  buffGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(top.position), 3));
  buffGeometry.computeVertexNormals();
  const meshTop = new THREE.Mesh(buffGeometry, binMaterial);
  meshTop.receiveShadow = true;
  meshTop.castShadow = true;

  const buffGeometry3 = new THREE.BufferGeometry();
  buffGeometry3.setAttribute('position', new THREE.BufferAttribute(new Float32Array(body.position), 3));
  buffGeometry3.computeVertexNormals();
  const meshBody = new THREE.Mesh(buffGeometry3, binMaterial);
  meshBody.receiveShadow = true;
  meshBody.castShadow = true;

  const buffGeometry2 = new THREE.BufferGeometry();
  buffGeometry2.setAttribute('position', new THREE.BufferAttribute(new Float32Array(bot.position), 3));
  buffGeometry2.computeVertexNormals();
  const meshBottom = new THREE.Mesh(buffGeometry2, binMaterial);
  meshBottom.receiveShadow = true;
  meshBottom.castShadow = true;

  return [
    meshTop,
    meshBody,
    meshBottom,
  ];
}
